import React, { useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

/* In-page anchors shown to the right of the wordmark on tablet+ desktop.
   These overlap with the SectionSpine on xl+, but the spine is hidden below
   xl so the navbar is the only in-page nav for 1024–1279px viewports. */
const inPageLinks = [];

const dropdowns = [
  {
    href: "/platform",
    label: "Platform",
    links: [
      { href: "/platform", label: "Platform overview" },
      { href: "/platform/privacy", label: "Privacy architecture" },
      { href: "/platform/statistical-quality", label: "Statistical quality" },
    ],
  },
  {
    href: "/solutions",
    label: "Solutions",
    links: [
      { href: "/solutions/emergency-preparedness", label: "Emergency preparedness" },
      { href: "/solutions/events-destinations", label: "Events & destinations", disabled: true },
      { href: "/solutions/cities-place-development", label: "Cities & place development", disabled: true },
      { href: "/solutions/transport-road-impact", label: "Transport & road impact", disabled: true },
      { href: "/solutions/population-visitor-insights", label: "Population & visitor insights", disabled: true },
      { href: "/solutions/bespoke-mobility-analysis", label: "Bespoke analysis", disabled: true },
    ],
  },
  {
    href: "/projects",
    label: "Projects",
    links: [
      { href: "/projects/arendalsuka", label: "Arendalsuka" },
      { href: "/projects/may-17th", label: "17 May in Oslo" },
      { href: "/projects/vaulen-school", label: "Vaulen School" },
      { href: "/projects/frogn-city-development", label: "Frogn and Drøbak" },
      { href: "/projects/road-closure-fornebu", label: "E18 lane closure at Fornebu" },
    ],
  },
];

const externalLinks = [
  { href: "https://docs.telcofy.ai", label: "Docs" },
  { href: "https://docs.telcofy.ai/blog", label: "Blog" },
  { href: "https://app.telcofy.ai", label: "CityOS" },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToContact = () => {
    const contactSection = document.getElementById("contact-section");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = "/#contact-section";
    }
  };

  const handleAnchor = (href) => {
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = `/${href}`;
    }
  };

  return (
    <nav className="fixed w-full z-50 bg-paper border-b border-paper-300 shadow-brand-sm">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-6">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2.5 flex-shrink-0">
          <img
            src="/brand/telcofy-mark.svg"
            alt="Telcofy"
            className="h-7 w-auto"
          />
          <span className="wordmark text-2xl">
            Telco<span className="fy">fy</span>
          </span>
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-1 ml-auto">
          {inPageLinks.map(({ href, label }) => (
            <button
              key={href}
              onClick={() => handleAnchor(href)}
              className="px-3 py-2 text-sm font-medium text-ink-muted rounded-md hover:bg-paper-200 hover:text-ink-soft transition-colors"
            >
              {label}
            </button>
          ))}
          {dropdowns.slice(0, 1).map(({ href, label, links }) => (
            <div key={href} className="relative group">
              <a href={href} className="px-3 py-2 text-sm font-medium text-ink-muted rounded-md hover:bg-paper-200 hover:text-ink-soft transition-colors inline-flex items-center gap-1">
                {label}<ChevronDown size={14} />
              </a>
              <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 absolute top-full left-0 pt-2 w-64 transition-all">
                <div className="bg-paper border border-paper-300 rounded-lg shadow-brand-card p-2">
                  <a href={href} className="block px-3 py-2 text-sm font-semibold text-ink rounded-md hover:bg-paper-200">All {label.toLowerCase()}</a>
                  <div className="h-px bg-paper-300 my-1" />
                  {links.map((link) => link.disabled
                    ? <span key={link.href} aria-disabled="true" className="block px-3 py-2 text-sm text-ink-faint opacity-50 cursor-not-allowed">{link.label} <small className="ml-1">Coming soon</small></span>
                    : <a key={link.href} href={link.href} className="block px-3 py-2 text-sm text-ink-muted rounded-md hover:bg-paper-200 hover:text-ink">{link.label}</a>)}
                </div>
              </div>
            </div>
          ))}
          {dropdowns.slice(1).map(({ href, label, links }) => (
            <div key={href} className="relative group">
              <a href={href} className="px-3 py-2 text-sm font-medium text-ink-muted rounded-md hover:bg-paper-200 hover:text-ink-soft transition-colors inline-flex items-center gap-1">
                {label}<ChevronDown size={14} />
              </a>
              <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 absolute top-full left-0 pt-2 w-64 transition-all">
                <div className="bg-paper border border-paper-300 rounded-lg shadow-brand-card p-2">
                  <a href={href} className="block px-3 py-2 text-sm font-semibold text-ink rounded-md hover:bg-paper-200">All {label.toLowerCase()}</a>
                  <div className="h-px bg-paper-300 my-1" />
                  {links.map((link) => link.disabled
                    ? <span key={link.href} aria-disabled="true" className="block px-3 py-2 text-sm text-ink-faint opacity-50 cursor-not-allowed">{link.label} <small className="ml-1">Coming soon</small></span>
                    : <a key={link.href} href={link.href} className="block px-3 py-2 text-sm text-ink-muted rounded-md hover:bg-paper-200 hover:text-ink">{link.label}</a>)}
                </div>
              </div>
            </div>
          ))}
          <span className="w-px h-5 bg-paper-300 mx-2" aria-hidden="true" />
          {externalLinks.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-sm font-medium text-ink-muted rounded-md hover:bg-paper-200 hover:text-ink-soft transition-colors"
            >
              {label}
            </a>
          ))}
          <button
            onClick={scrollToContact}
            className="ml-2 px-4 py-2 text-sm font-semibold text-white bg-brand-500 rounded-md hover:bg-brand-600 transition-colors"
          >
            Contact
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-ink-soft hover:bg-paper-200 rounded-md transition-colors"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-paper border-b border-paper-300 shadow-brand-md py-3 px-6 flex flex-col gap-1">
          {inPageLinks.map(({ href, label }) => (
            <button
              key={href}
              onClick={() => {
                handleAnchor(href);
                setIsMenuOpen(false);
              }}
              className="px-3 py-2.5 text-sm font-semibold text-ink-soft rounded-md hover:bg-paper-200 transition-colors text-left"
            >
              {label}
            </button>
          ))}
          {dropdowns.slice(0, 1).map(({ href, label, links }) => (
            <div key={href} className="py-1">
              <a href={href} onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 text-sm font-semibold text-ink">{label}</a>
              <div className="pl-3 border-l border-paper-300 ml-3">
                {links.map((link) => link.disabled
                  ? <span key={link.href} aria-disabled="true" className="block px-3 py-1.5 text-sm text-ink-faint opacity-50">{link.label} · Coming soon</span>
                  : <a key={link.href} href={link.href} onClick={() => setIsMenuOpen(false)} className="block px-3 py-1.5 text-sm text-ink-muted">{link.label}</a>)}
              </div>
            </div>
          ))}
          {dropdowns.slice(1).map(({ href, label, links }) => (
            <div key={href} className="py-1">
              <a href={href} onClick={() => setIsMenuOpen(false)} className="block px-3 py-2 text-sm font-semibold text-ink">{label}</a>
              <div className="pl-3 border-l border-paper-300 ml-3">
                {links.map((link) => link.disabled
                  ? <span key={link.href} aria-disabled="true" className="block px-3 py-1.5 text-sm text-ink-faint opacity-50">{link.label} · Coming soon</span>
                  : <a key={link.href} href={link.href} onClick={() => setIsMenuOpen(false)} className="block px-3 py-1.5 text-sm text-ink-muted">{link.label}</a>)}
              </div>
            </div>
          ))}
          <div className="h-px bg-paper-300 my-2" aria-hidden="true" />
          {externalLinks.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2.5 text-sm font-semibold text-ink-soft rounded-md hover:bg-paper-200 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              {label}
            </a>
          ))}
          <button
            onClick={() => {
              scrollToContact();
              setIsMenuOpen(false);
            }}
            className="mt-1 px-3 py-2.5 text-sm font-semibold text-white bg-brand-500 rounded-md hover:bg-brand-600 transition-colors text-left"
          >
            Contact
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
